import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Eye } from "lucide-react";
import { resolveImageUrl } from "./ImageUpload";
import { openAdminProductEdit, openPublicProduct } from "../lib/openAdminProduct";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Simple in-memory cache so we don't refetch the same product on every hover.
 * Keyed by product_id; value is the product object (or `null` if fetch failed).
 */
const productCache = new Map();
const inFlight = new Map();

async function fetchProduct(productId) {
  if (!productId) return null;
  if (productCache.has(productId)) return productCache.get(productId);
  if (inFlight.has(productId)) return inFlight.get(productId);
  const p = axios.get(`${API}/products/${productId}`, { withCredentials: true })
    .then(r => { productCache.set(productId, r.data); return r.data; })
    .catch(() => { productCache.set(productId, null); return null; })
    .finally(() => { inFlight.delete(productId); });
  inFlight.set(productId, p);
  return p;
}

/**
 * Hover preview + click-to-edit for a product cell.
 *
 * Props:
 *   productId      — required. Used to fetch details + navigate.
 *   fallbackName   — shown as label when the API call hasn't returned yet.
 *   fallbackSku    — small monospace sub-label (e.g., internal SKU).
 *   showEye        — render the "eye" icon that opens the public page (default true).
 *   children       — override the visible label (otherwise `fallbackName` is used).
 *
 * Behaviour:
 *   • Hover: 250 ms after mouse-enter, floating 260×260 card appears near the row.
 *   • Click on the label: opens Admin → Товары with edit modal for this product.
 *   • Click on the eye icon: opens `/app/catalog/{id}` in a new tab.
 *   • No `productId` → renders `children` / `fallbackName` as plain text.
 */
export default function ProductHoverPreview({
  productId, fallbackName, fallbackSku, showEye = true, children,
  labelStyle,
}) {
  const [hoverPos, setHoverPos] = useState(null); // { x, y }
  const [product, setProduct] = useState(null);
  const timerRef = useRef(null);
  const anchorRef = useRef(null);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  // Warm the cache when the label is being hovered — but only show the popup
  // after a short delay so scanning the table doesn't flicker previews.
  const onEnter = (e) => {
    if (!productId) return;
    const rect = anchorRef.current?.getBoundingClientRect();
    // Anchor the preview to the right of the row, vertically centred on the cell.
    const x = (rect ? rect.right : e.clientX) + 12;
    const y = rect ? rect.top + rect.height / 2 : e.clientY;
    timerRef.current = setTimeout(async () => {
      const p = await fetchProduct(productId);
      setProduct(p);
      setHoverPos({ x, y });
    }, 220);
  };

  const onLeave = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setHoverPos(null);
  };

  const openEdit = (e) => {
    if (!productId) return;
    e.stopPropagation();
    openAdminProductEdit(productId);
  };

  const openPublic = (e) => {
    if (!productId) return;
    e.stopPropagation();
    openPublicProduct(productId);
  };

  const label = children ?? fallbackName ?? "—";
  const isLinked = !!productId;

  return (
    <span
      ref={anchorRef}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      style={{ display: "inline-flex", alignItems: "center", gap: 8, ...(labelStyle || {}) }}
    >
      <span
        onClick={openEdit}
        data-testid={isLinked ? `hover-product-open-${productId}` : undefined}
        title={isLinked ? "Открыть карточку товара" : ""}
        style={{
          cursor: isLinked ? "pointer" : "default",
          color: isLinked ? "#111" : "#64748b",
          borderBottom: isLinked ? "1px dashed rgba(197,160,89,0.55)" : "none",
          transition: "color 0.15s",
        }}
      >
        {label}
      </span>
      {isLinked && showEye && (
        <button
          type="button"
          onClick={openPublic}
          data-testid={`hover-product-eye-${productId}`}
          title="Открыть публичную карточку в новой вкладке"
          style={{
            background: "transparent", border: "none", padding: 2, cursor: "pointer",
            color: "#94a3b8", display: "inline-flex", alignItems: "center",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = "#C5A059"; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = "#94a3b8"; }}
        >
          <Eye size={14} />
        </button>
      )}

      {hoverPos && isLinked && createPortal(
        <HoverCard
          x={hoverPos.x}
          y={hoverPos.y}
          product={product}
          fallbackName={fallbackName}
          fallbackSku={fallbackSku}
        />,
        document.body
      )}
    </span>
  );
}

function HoverCard({ x, y, product, fallbackName, fallbackSku }) {
  // Clamp to viewport so the popup never overflows.
  const W = 260;
  const H = 340;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  let left = x;
  if (left + W + 8 > vw) left = Math.max(8, x - W - 40); // flip to left of anchor
  let top = y - H / 2;
  if (top < 8) top = 8;
  if (top + H + 8 > vh) top = vh - H - 8;

  const img = product?.images?.[0];
  const name = product?.name || fallbackName || "Загрузка…";
  const ourSku = product?.our_sku || fallbackSku;
  const factory = product?.sku;
  const brand = product?.brand;

  return (
    <div
      data-testid="product-hover-card"
      style={{
        position: "fixed",
        left, top,
        width: W,
        zIndex: 1200,
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        boxShadow: "0 12px 32px rgba(15,23,42,0.18)",
        fontFamily: "Manrope",
        pointerEvents: "none",
        overflow: "hidden",
      }}
    >
      <div style={{
        width: "100%", height: 260, background: "#f5f5f4",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {img ? (
          <img
            src={resolveImageUrl(img)}
            alt={name}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <span style={{ fontSize: 11, color: "#a3a3a2", letterSpacing: 0.4 }}>
            {product === null ? "нет фото" : "…"}
          </span>
        )}
      </div>
      <div style={{ padding: "10px 12px 12px" }}>
        <div style={{
          fontFamily: "Cormorant Garamond", fontSize: 17, fontWeight: 600,
          color: "#0A0A0A", lineHeight: 1.2,
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}>
          {name}
        </div>
        {brand && (
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 3, letterSpacing: 0.3 }}>
            {brand}
          </div>
        )}
        <div style={{ display: "flex", gap: 10, marginTop: 6, flexWrap: "wrap", fontSize: 11, color: "#64748b" }}>
          {ourSku && (
            <span>SKU: <b style={{ fontFamily: "monospace", color: "#C5A059" }}>{ourSku}</b></span>
          )}
          {factory && (
            <span>Фабр.: <b style={{ fontFamily: "monospace", color: "#374151" }}>{factory}</b></span>
          )}
        </div>
      </div>
    </div>
  );
}
